import { Component, Inject, OnInit } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Moment } from 'moment';
import { ContaTev } from 'src/app/base/domain/conta-tev.model';
import { LancamentoDespesa } from 'src/app/base/domain/lancamento-despesa.model';
import { Posto } from 'src/app/base/domain/posto.model';
import { CategoriaDespesa } from 'src/app/base/domain/enum/categoria-despesa.enum';
import { DESCRICAO_FORMA_PAGAMENTO_DESPESA, FormaPagamentoDespesa } from 'src/app/base/domain/enum/forma-pagamento-despesa.enum';
import {
  DESCRICAO_SITUACAO_LANCAMENTO_DESPESA,
  SituacaoLancamentoDespesa,
} from 'src/app/base/domain/enum/situacao-lancamento-despesa.enum';
import { TipoDespesa } from 'src/app/base/domain/enum/tipo-despesa.enum';

export interface LancamentoDespesaFormData {
  action: string;
  lancamento: LancamentoDespesa;
  postos: Posto[];
  contas: ContaTev[];
}

@Component({
  selector: 'app-lancamento-despesa-form',
  templateUrl: './lancamento-despesa-form.component.html',
  styleUrls: ['./lancamento-despesa-form.component.css'],
})
export class LancamentoDespesaFormComponent implements OnInit {
  lancamento: LancamentoDespesa;
  postoSelecionado: Posto = null;
  erro: string = null;

  tiposDespesa: TipoDespesa[] = Object.values(TipoDespesa);
  categorias: CategoriaDespesa[] = Object.values(CategoriaDespesa);
  formasPagamento: FormaPagamentoDespesa[] = Object.values(FormaPagamentoDespesa);
  situacoes: SituacaoLancamentoDespesa[] = Object.values(SituacaoLancamentoDespesa);

  readonly FORMA_BANCO = FormaPagamentoDespesa.BANCO;
  readonly SITUACAO_PAGO = SituacaoLancamentoDespesa.PAGO;

  constructor(
    public dialogRef: MatDialogRef<LancamentoDespesaFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LancamentoDespesaFormData,
  ) {
    this.lancamento = data.lancamento ? { ...data.lancamento } : this.novoLancamento();
    this.postoSelecionado = this.data.postos?.find((posto: Posto) => posto.id === this.lancamento.postoId) ?? null;
  }

  ngOnInit(): void {}

  get titulo(): string {
    return this.data.action === 'editar' ? 'Editar Lançamento de Despesa' : 'Novo Lançamento de Despesa';
  }

  descricaoFormaPagamento(formaPagamento: FormaPagamentoDespesa): string {
    return DESCRICAO_FORMA_PAGAMENTO_DESPESA[formaPagamento];
  }

  descricaoSituacao(situacao: SituacaoLancamentoDespesa): string {
    return DESCRICAO_SITUACAO_LANCAMENTO_DESPESA[situacao];
  }

  descricaoContaTev(conta: ContaTev): string {
    return `${conta.identificador} - Ag. ${conta.agencia} / C. ${conta.conta}`;
  }

  compararContas(conta: ContaTev, outra: ContaTev): boolean {
    return conta?.id === outra?.id;
  }

  onDataDespesaChange(event: MatDatepickerInputEvent<Moment>): void {
    this.lancamento.dataDespesa = event.value?.toDate() || null;
  }

  onDataPagamentoChange(event: MatDatepickerInputEvent<Moment>): void {
    this.lancamento.dataPagamento = event.value?.toDate() || null;
  }

  onFormaPagamentoChange(): void {
    if (this.lancamento.formaPagamento !== FormaPagamentoDespesa.BANCO) {
      this.lancamento.contaTev = null;
    }
  }

  onSituacaoChange(): void {
    if (this.lancamento.situacao !== SituacaoLancamentoDespesa.PAGO) {
      this.lancamento.dataPagamento = null;
    }
  }

  onPostoChange(): void {
    this.lancamento.postoId = this.postoSelecionado ? this.postoSelecionado.id : null;
    this.lancamento.postoDescricao = this.postoSelecionado ? this.postoSelecionado.descricao : null;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    this.erro = this.validar();

    if (this.erro) {
      return;
    }

    this.dialogRef.close(this.lancamento);
  }

  private validar(): string {
    if (!this.lancamento.dataDespesa) {
      return 'Informe a data da despesa.';
    }

    if (!this.lancamento.descricao) {
      return 'Informe a descrição da despesa.';
    }

    if (!this.lancamento.tipoDespesa) {
      return 'Informe o tipo da despesa.';
    }

    if (!this.lancamento.valor || this.lancamento.valor <= 0) {
      return 'Informe um valor maior que zero.';
    }

    if (!this.lancamento.formaPagamento) {
      return 'Informe a forma de pagamento.';
    }

    if (this.lancamento.formaPagamento === FormaPagamentoDespesa.BANCO && !this.lancamento.contaTev) {
      return 'Informe a conta bancária para despesas pagas por banco.';
    }

    return null;
  }

  private novoLancamento(): LancamentoDespesa {
    const lancamento = new LancamentoDespesa();
    lancamento.dataDespesa = new Date();
    lancamento.tipoDespesa = TipoDespesa.ADMINISTRATIVA;
    lancamento.formaPagamento = FormaPagamentoDespesa.CAIXA;
    lancamento.situacao = SituacaoLancamentoDespesa.PENDENTE;
    lancamento.valor = 0;
    return lancamento;
  }
}
